import { Fragment } from "react";
import { Link } from "react-router-dom";

import { defeatedBy, villainAnchor } from "../caseload";
import { usePickedRandom } from "../usePickedRandom";

/**
 * The stills the page banner rotates between, with the film-still caption that
 * belongs to each. Bear-forward frames, all of them composed with him right of
 * centre: the banner's scrim is anchored to the left, under the copy, so the
 * right of the frame is the half that stays a photograph. Exported so a test
 * can assert the banner shows one of them rather than pinning the page to
 * whichever variant a stubbed `Math.random` happened to return.
 */
export const BANNER_STILLS = [
  {
    id: "patrol",
    src: "/bear/banner-features-patrol.webp",
    caption: "BIG BEAR — the night round, Hall 3",
  },
  {
    // He is asleep across the doorway in this one, which is the point: the
    // caption says where he is, not that he is awake.
    id: "doorway",
    src: "/bear/banner-features-doorway.webp",
    caption: "BIG BEAR — the darkroom door, 04:00",
  },
  {
    id: "wreckage",
    src: "/bear/banner-features-wreckage.webp",
    caption: "BIG BEAR — after the raid, Block 9",
  },
  {
    id: "swarm",
    src: "/bear/banner-features-swarm.webp",
    caption: "BIG BEAR vs THE SWARM — scene 07",
  },
] as const;

interface Group {
  id: string;
  title: string;
  intro: string;
  items: string[];
}

const groups: Group[] = [
  {
    id: "storage",
    title: "Storage",
    intro: "Back up to whatever you already have — no dedicated appliance required.",
    items: [
      "Local drive, or a NAS/SMB share over a plain UNC path",
      "S3-compatible cloud: Backblaze B2, AWS, Wasabi, MinIO, Ceph",
      "One bucket can hold several vaults under different key prefixes",
      "Or sign in to Google Drive, OneDrive or Dropbox — no keys to copy, and the app only ever touches what it puts there",
      "No iCloud backend, because Apple publishes no API for it — but iCloud for Windows syncs to an ordinary folder, so point a Folder location at it and keep that folder set to stay on the device",
      "Uploads run several at a time, with a default and ceiling chosen per destination — an object store takes more than a sync provider tolerates",
      "Objects over 128 MiB upload in parts; a failed multipart upload is aborted, not left billing you",
    ],
  },
  {
    id: "efficiency",
    title: "Efficiency",
    intro: "Deduplication isn't a setting to turn on — it's how the format works.",
    items: [
      "FastCDC content-defined chunking, ~1 MiB average chunk size",
      "Incremental backups fall out of chunking: an edit only touches nearby chunks",
      "Optional per-pack Reed–Solomon parity (5% by default) survives partial corruption without a second copy",
    ],
  },
  {
    id: "security",
    title: "Security",
    intro: "Your passphrase never sees your data directly, and rotating it is cheap.",
    items: [
      "AES-256-GCM authenticated encryption, per blob, with per-pack keys",
      "A random master secret, wrapped by your passphrase — never derived from it",
      "Changing your passphrase rewraps a few hundred bytes; it never re-encrypts your data",
      "Chunk IDs are keyed, so nobody holding the vault can test whether it contains a file they already have",
      "The recovery key is shown once, behind an explicit acknowledgement — not a modal a stray click can dismiss",
    ],
  },
  {
    id: "integrity",
    title: "Integrity",
    intro: "A backup that fails quietly is worse than no backup.",
    items: [
      "AEAD tags plus keyed chunk IDs, re-verified after every decryption",
      "verify reads and authenticates everything in a vault, not just its index",
      "Every single-bit flip in a sealed blob is tested for in CI — never a partial or unverified result",
      "Every prefix of a sealed blob is rejected, so a truncated write can't pass as valid",
    ],
  },
  {
    id: "retention",
    title: "Retention",
    intro: "Old snapshots go away safely, on rules you set once.",
    items: [
      "Independent, additive tiers: keep-last, keep-within, daily, weekly, monthly, yearly",
      "Evaluated per job per machine, so two computers running a job called \"photos\" never compete for the same budget",
      "Running with no tier at all is refused with an error, not silently pruning nothing",
      "prune (decide what to keep) and gc (reclaim the space) are deliberately separate steps",
      "A pack is only deleted once every chunk inside it is unreferenced by every remaining snapshot",
    ],
  },
  {
    id: "operations",
    title: "Operations",
    intro: "The same engine, whether you're at the keyboard or it's running unattended.",
    items: [
      "A CLI (bigbear) and a Windows desktop app, both thin wrappers over one engine — nothing can drift between them",
      "Long operations run off the UI thread; a running backup is always safely stoppable",
      "Automatic updates you can set to install silently or just notify you",
      "Progress reporting and cancellation built into the engine, not bolted onto the UI",
    ],
  },
];

export function Features() {
  // Held for the life of the page, so a re-render never swaps the still out
  // from under the reader — the same reason the home hero does it this way.
  const still = usePickedRandom(BANNER_STILLS);

  return (
    <>
      {/* `hero--cine` as well as `hero--banner`: the banner is the home hero's
          construction — flex column, bottom-anchored, chip in a corner — and
          `hero--banner` is only what differs from it. */}
      <section className="hero hero--cine hero--banner marketing__bleed">
        {/* Decorative: the still sets a mood, the copy over it carries the
            meaning. The scrim between them is what makes that copy legible. */}
        <img className="hero__still" src={still.src} alt="" decoding="async" fetchPriority="high" />
        <div className="hero__scrim hero__scrim--side" aria-hidden="true" />

        <div className="hero__content bleed-inner">
          <div className="hero__copy">
            <h1>Everything Big Bear Backup does</h1>
            <p className="muted">
              Grouped by what it's protecting you from — not a spec sheet.
            </p>
            <p className="banner-line">Big Bear's full case file.</p>
          </div>
        </div>

        <p className="hero__chip">{still.caption}</p>
      </section>

      <nav className="features-jump">
        {groups.map((g) => (
          <a key={g.id} href={`#${g.id}`}>
            {g.title}
          </a>
        ))}
      </nav>

      {groups.map((g) => (
        <section className="panel features-group" id={g.id} key={g.id}>
          <h2>{g.title}</h2>
          <p className="muted">{g.intro}</p>
          <ul>
            {g.items.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          {g.id === "storage" && (
            <p className="muted">
              Thread and pack-size defaults, and why they differ per destination:{" "}
              <Link to="/providers">Providers, threads and pack size</Link>.
            </p>
          )}
          <Defeats groupId={g.id} />
        </section>
      ))}

      <p className="muted">
        Want the actual bytes on disk? <Link to="/spec">Format spec, for nerds.</Link>
      </p>
    </>
  );
}

/**
 * One line crediting the group with the case files it closes, each name linking
 * back to that villain's card on the home page. Renders nothing for the groups
 * (storage, retention, operations) that no villain is filed under — a "Defeats:"
 * with nothing after it would be worse than no line.
 *
 * These are router `Link`s, not `<a href="/#…">`: the latter is a full page
 * reload out of the SPA. `ScrollToHash` in `App.tsx` is what makes the fragment
 * half of the destination work, since `pushState` on its own never scrolls.
 */
function Defeats({ groupId }: { groupId: string }) {
  const villains = defeatedBy(groupId);
  if (villains.length === 0) return null;

  return (
    <p className="features-group__defeats">
      <span className="features-group__defeats-label">Defeats:</span>{" "}
      {villains.map((villain, i) => (
        <Fragment key={villain.id}>
          {i > 0 && ", "}
          <Link to={`/#${villainAnchor(villain.id)}`}>{villain.name}</Link>
        </Fragment>
      ))}
    </p>
  );
}
